import React, { useLayoutEffect, useRef, useState } from 'react';
import css from './MatchingDialog.css';
import classNames from 'classnames';
import ModalPortal from '../ModalPortal/ModalPortal';
import { categories, MatchingDialogContext } from './configs';
import { useSelector } from 'react-redux';
import SectionRadius from './sections/SectionRadius';
import SectionMobility from './sections/SectionMobility';
import SectionAvailability from './sections/SectionAvailability';
import Alert from '../Alert/Alert';
import config from '../../config';
import SectionInterests from './sections/SectionInterests';
import SectionDisciplines from './sections/SectionDisciplines';
import SectionLevels from './sections/SectionLevels';
import SectionMinimumAge from './sections/SectionMinimumAge';
import SectionQualification from './sections/SectionQualification';
import SectionLocation from './sections/SectionLocation';
import SectionSkills from './sections/SectionSkills';
import FeedbackForSection from '../FeedbackForSection/FeedbackForSection';
import { checkIfListingIsAnonymous, shouldListingBeDeanonymizedForUser } from '../../util/anonym';
import { getRiderAndHorseListings } from './utils';
import { FEEDBACK_SECTION_ID_MATCHING } from '../FeedbackForSection/configs';

const {
    listingTypeHorse,
    listingTypeRider,
    listingTypePrivateHorse,
    listingTypePrivateRider,
} = config;

const MatchingDialog = ({ modalOpen, onClose, currentPageListing, viewerListing, scoreData }) => {
    const [categorySelected, setSelectedCategory] = useState(null);
    const [showAlert, setShowAlert] = useState(false);

    const sectionRef = useRef(null);

    useLayoutEffect(() => {
        setShowAlert(sectionRef.current?.childElementCount === 0);
    }, [categorySelected]);

    const currentUser = useSelector(s => s.user.currentUser);
    const allEntities = useSelector(s => s.marketplaceData.entities || {});

    const { uuid: currentListingId } = currentPageListing.id;
    const { publicData } = currentPageListing?.attributes || {};
    const { type, userRepresentationId, hasDefaultAvatar } = publicData || {};

    const isHorseListing = type === listingTypeHorse || type === listingTypePrivateHorse;
    const isRiderListing = type === listingTypeRider || type === listingTypePrivateRider;

    const { horseListing, riderListing } = getRiderAndHorseListings({
        currentPageListing,
        viewerListing,
    });

    const horseListingTitle = horseListing?.attributes?.title;

    const horseImage = (isHorseListing ? currentPageListing : viewerListing)?.attributes?.publicData
        ?.mainListingImage;

    const isAnonymousListing = checkIfListingIsAnonymous(currentPageListing);
    const isDeanonymizedForUser = shouldListingBeDeanonymizedForUser(
        currentPageListing,
        currentUser
    );

    const riderId = isRiderListing ? userRepresentationId : currentUser?.id?.uuid;
    const riderMarketplaceData = allEntities?.user ? allEntities?.user[riderId] : null;
    const riderProfileImageId = riderMarketplaceData?.relationships?.profileImage?.data?.id?.uuid;
    const riderImageVariants = riderProfileImageId
        ? allEntities?.image?.[riderProfileImageId]?.attributes?.variants
        : null;
    const riderIsCurrentUser = riderId === currentUser?.id?.uuid;
    const currentUserVariants = currentUser?.profileImage?.attributes?.variants || {};
    const currentUserFirstName = currentUser?.attributes?.profile?.firstName || '';
    const riderMarketplaceDisplayName =
        riderMarketplaceData?.attributes?.profile?.displayName || '';
    const riderProfileImage = riderIsCurrentUser
        ? currentUserVariants['square-small']?.url || currentUserVariants['square-small2x']?.url
        : riderImageVariants?.['square-small']?.url ||
          riderImageVariants?.['square-small2x']?.url ||
          null;

    const [riderDisplayName] = (riderIsCurrentUser
        ? currentUserFirstName
        : riderMarketplaceDisplayName
    ).split(' ');

    const currentPageListingIsAnonym =
        isAnonymousListing && !isDeanonymizedForUser && !hasDefaultAvatar;

    const currentListingTitle = isHorseListing ? horseListingTitle : riderDisplayName;

    const errorAlert = scoreData.message ? (
        <Alert
            header="The following error occurred"
            description={scoreData.message}
            type="failure"
        />
    ) : null;

    const handleCategorySelecting = categoryId => {
        setSelectedCategory(prev => (prev === categoryId ? null : categoryId));
    };

    return (
        <ModalPortal
            id="switch-user-role-modal"
            isOpen={modalOpen}
            onClose={onClose}
            containerClassName={css.modalContainer}
            contentClassName={css.modalContent}
            containerClassNameJoined
            isSticky
        >
            <h3 className={css.heading}>Matching</h3>
            <p className={css.description}>
                Erfahre, welche Faktoren zu deiner Übereinstimmung mit {currentListingTitle} führen.
            </p>
            {errorAlert || (
                <>
                    <nav className={css.badgesHolder}>
                        {categories.map(category => (
                            <span
                                key={category.id}
                                onClick={() => handleCategorySelecting(category.id)}
                                className={classNames(css.badge, {
                                    [css.selected]: category.id === categorySelected,
                                })}
                            >
                                {category.label}
                            </span>
                        ))}
                    </nav>
                    <MatchingDialogContext.Provider
                        value={{
                            scoreData,
                            categorySelected,
                            currentUser,
                            currentPageListing,
                            viewerListing,
                            horseListing,
                            riderListing,
                            horseImage: {
                                url: horseImage,
                                isAnonymousListing: isHorseListing && currentPageListingIsAnonym,
                                entityName: horseListingTitle,
                            },
                            riderProfileImage: {
                                url: riderProfileImage,
                                isAnonymousListing: isRiderListing && currentPageListingIsAnonym,
                                entityName: riderDisplayName,
                            },
                        }}
                    >
                        <section ref={sectionRef} className={css.sectionsHolder}>
                            <SectionLocation />
                            <SectionRadius />
                            <SectionMobility />
                            <SectionAvailability />
                            <SectionInterests />
                            <SectionDisciplines />
                            <SectionLevels />
                            <SectionMinimumAge />
                            <SectionQualification />
                            <SectionSkills />
                        </section>
                        {showAlert && (
                            <Alert
                                showAsPlainText
                                type="warning"
                                header="Kein Kriterium gefunden"
                                description="Es gibt kein Kriterium, das auf die ausgewählten Filter zutrifft."
                                closeAllowed={false}
                            />
                        )}
                        <FeedbackForSection
                            rootClassName={css.feedbackRoot}
                            heading="War diese Analyse hilfreich?"
                            analysisId={isRiderListing ? userRepresentationId : currentListingId}
                            sectionId={FEEDBACK_SECTION_ID_MATCHING}
                            extraParams={{
                                type: isRiderListing ? 'profileId' : 'listingId',
                            }}
                        />
                    </MatchingDialogContext.Provider>
                </>
            )}
        </ModalPortal>
    );
};

export default MatchingDialog;
